// =============================
// libs/layers/common/media-layer/composables/useImageAspectRatio.ts
// Reusable composable to measure an image, classify its aspect ratio,
// and produce Tailwind aspect-* + object-fit classes.
// =============================
import { computed, ref } from 'vue'

export type AspectCategory = '1:1' | '16:9' | '9:16' | '4:3' | '3:4' | '21:9' | 'other'

export interface UseImageAspectRatioOptions {
  /** Acceptable % error when matching a ratio (e.g. 0.15 = 15%) */
  tolerance?: number
  /** Map a category to a container class (Tailwind aspect-* or arbitrary) */
  mapping?: Partial<Record<AspectCategory, string>>
  /** Prefer object-contain instead of object-cover */
  preferContain?: boolean
  /** Enable gradient background for empty spaces */
  useGradientBackground?: boolean
}

export interface UseImageAspectRatio {
  width: Readonly<{ value: number }>
  height: Readonly<{ value: number }>
  ratio: Readonly<{ value: number }>
  category: Readonly<{ value: AspectCategory }>
  isLoaded: Readonly<{ value: boolean }>
  error: Readonly<{ value: string | null }>
  /** Measure an image URL and classify it */
  measure: (src: string) => Promise<void>
  /** Container class (e.g. aspect-video / aspect-square / aspect-[4/3]) */
  aspectClass: Readonly<{ value: string }>
  /** object-cover or object-contain */
  objectFitClass: Readonly<{ value: string }>
  /** Whether to use gradient background */
  needsGradientBackground: Readonly<{ value: boolean }>
  /** Gradient background class */
  gradientBackgroundClass: Readonly<{ value: string }>
}

export function useImageAspectRatio(options: UseImageAspectRatioOptions = {}): UseImageAspectRatio {
  const tolerance = options.tolerance ?? 0.15 // Increased tolerance for better matching
  const width = ref(0)
  const height = ref(0)
  const ratio = ref(1)
  const category = ref<AspectCategory>('1:1') // Default to square
  const isLoaded = ref(false)
  const error = ref<string | null>(null)

  const defaultMapping: Record<AspectCategory, string> = {
    '1:1': 'aspect-square',
    '16:9': 'aspect-video',
    '9:16': 'aspect-[9/16]',
    '4:3': 'aspect-[4/3]',
    '3:4': 'aspect-[3/4]',
    '21:9': 'aspect-[21/9]',
    other: 'aspect-video', // Default to 16:9 for unknown ratios
  }

  function isClose(a: number, b: number) {
    return Math.abs(a - b) <= b * tolerance
  }

  function classify(r: number): AspectCategory {
    // Define target ratios in order of preference
    const targetRatios: Array<{ ratio: number; category: AspectCategory }> = [
      { ratio: 1, category: '1:1' }, // Square
      { ratio: 16 / 9, category: '16:9' }, // Landscape video (1.78)
      { ratio: 9 / 16, category: '9:16' }, // Portrait video (0.56)
      { ratio: 4 / 3, category: '4:3' }, // Traditional landscape (1.33)
      { ratio: 3 / 4, category: '3:4' }, // Traditional portrait (0.75)
      { ratio: 21 / 9, category: '21:9' }, // Ultra-wide (2.33)
    ]

    // Find the closest matching ratio
    let closestMatch = targetRatios[0]
    let smallestDifference = Math.abs(r - closestMatch.ratio)

    for (const target of targetRatios) {
      const difference = Math.abs(r - target.ratio)
      if (difference < smallestDifference) {
        smallestDifference = difference
        closestMatch = target
      }
    }

    // Use the closest match regardless of tolerance for better classification
    // But apply some logic for edge cases
    if (r > 2.0) {
      // Very wide images (like 1285x536 = 2.4) should use 21:9 or 16:9
      return r > 2.2 ? '21:9' : '16:9'
    } else if (r < 0.6) {
      // Very tall images should use 9:16
      return '9:16'
    }

    return closestMatch.category
  }

  async function measure(src: string) {
    isLoaded.value = false
    error.value = null

    if (!src) {
      error.value = 'Empty src'
      return
    }

    // SSR-safe guard
    if (typeof window === 'undefined') {
      // Defer to client; nothing to measure on server
      isLoaded.value = false
      return
    }

    try {
      const img = new Image()
      img.decoding = 'async'
      img.src = src

      if (img.complete && img.naturalWidth && img.naturalHeight) {
        width.value = img.naturalWidth
        height.value = img.naturalHeight
      } else {
        await img.decode().catch(
          () =>
            new Promise<void>((resolve, reject) => {
              img.onload = () => resolve()
              img.onerror = () => reject(new Error('Failed to load image'))
            })
        )
        width.value = img.naturalWidth
        height.value = img.naturalHeight
      }

      ratio.value = width.value / Math.max(1, height.value)
      category.value = classify(ratio.value)
      isLoaded.value = true
    } catch (e: any) {
      error.value = e?.message ?? 'Unknown image error'
      isLoaded.value = true
      // keep defaults so UI can still render a fallback
    }
  }

  const aspectClass = computed(() => {
    const map = { ...defaultMapping, ...(options.mapping ?? {}) }
    return map[category.value] || 'aspect-square'
  })

  const objectFitClass = computed(() => {
    // Always use object-contain when gradient background is enabled
    if (options.useGradientBackground) {
      return 'object-contain'
    }
    return options.preferContain ? 'object-contain' : 'object-cover'
  })

  const needsGradientBackground = computed(() => {
    if (!options.useGradientBackground || !isLoaded.value) return false

    // Get the target ratio for the classified category
    const targetRatios: Record<AspectCategory, number> = {
      '1:1': 1,
      '16:9': 16 / 9,
      '9:16': 9 / 16,
      '4:3': 4 / 3,
      '3:4': 3 / 4,
      '21:9': 21 / 9,
      other: 16 / 9,
    }

    const targetRatio = targetRatios[category.value]
    const actualRatio = ratio.value

    // If the actual ratio doesn't match the target ratio closely, we need gradient background
    return !isClose(actualRatio, targetRatio)
  })

  const gradientBackgroundClass = computed(() => {
    if (!needsGradientBackground.value) return ''

    // Return a subtle gradient background class
    return 'bg-gradient-to-br from-gray-50 to-gray-100'
  })

  return {
    width,
    height,
    ratio,
    category,
    isLoaded,
    error,
    measure,
    aspectClass,
    objectFitClass,
    needsGradientBackground,
    gradientBackgroundClass,
  }
}
